import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Avatar from '@mui/material/Avatar';
import { FC, useEffect, useLayoutEffect, useMemo, useRef, useState, MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { logoutRequest, getAvailableReportTypesRequest } from 'redux/reducers/auth/reducer';
import { Badge, Tooltip } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
	getMessages,
	getNewMessageCount,
	getOrganization,
	getUserInfo,
} from 'redux/reducers/user/selectors';
import {
	getMessagesRequest,
	getNewMessageCountRequest,
	getUserInfoRequest,
} from 'redux/reducers/user/reducer';
import useInterval from 'use-interval';
import {
	getAuthRefreshToken,
	getAvailableStandardVatReportTypes,
	getAvailableVatReportTypes,
	getMerchantId,
} from 'redux/reducers/auth/selectors';
import { getLastInvoiceStatusRequest } from 'redux/reducers/invoices/reducer';
import { getLastInvoiceStatus } from 'redux/reducers/invoices/selectors';
import { getIsSwissPostIntegrationEnabledRequest } from 'redux/reducers/shipments/reducer';
import { UserMenu } from './UserMenu';
import { MessagesMenu } from './MessagesMenu';
import { PastDueBanner } from './PastDueBanner';
import { RelinkPaymentMethodBanner } from './RelinkPaymentMethodBanner';
import {
	NavigationItems,
	getNavEntries,
	isNavEntryActive,
	NavEntryButton,
} from './NavigationItems';
import { getDashboardServicesRequest } from 'redux/reducers/dashboard/reducer';
import { getDashboardServices } from 'redux/reducers/dashboard/selectors';
import { getSummaryRequest } from 'redux/reducers/paymentMethods/reducer';
import {
	getPaymentMethodsSummary,
	getPaymentMethodsSummaryLoading,
	getPaymentMethodsSummaryMerchantId,
} from 'redux/reducers/paymentMethods/selectors';

const ResponsiveAppBar: FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	const userinfo = useSelector(getUserInfo);
	const xMerchantId = useSelector(getMerchantId);
	const organization = useSelector(getOrganization);
	const newMessageCount = useSelector(getNewMessageCount);
	const refreshToken = useSelector(getAuthRefreshToken);
	const messages = useSelector(getMessages);
	const dashboardServices = useSelector(getDashboardServices);
	const isShipmentsEnabled = dashboardServices?.swissPostEnabled || false;
	const isEuDdpEnabled = Boolean(dashboardServices?.euDdpEnabled || xMerchantId?.includes('GB'));

	const availableStandardVatReportTypes = useSelector(getAvailableStandardVatReportTypes);
	const availableVatReportTypes = useSelector(getAvailableVatReportTypes);

	const lastInvoiceStatus = useSelector(getLastInvoiceStatus);
	const paymentMethodsSummary = useSelector(getPaymentMethodsSummary);
	const paymentMethodsSummaryLoading = useSelector(getPaymentMethodsSummaryLoading);
	const paymentMethodsSummaryMerchantId = useSelector(getPaymentMethodsSummaryMerchantId);

	useEffect(() => {
		if (!userinfo) dispatch(getUserInfoRequest());
	}, [dispatch, userinfo]);

	useEffect(() => {
		if (!organization) dispatch(getUserInfoRequest());
		dispatch(getNewMessageCountRequest());
		dispatch(getMessagesRequest());
		dispatch(getLastInvoiceStatusRequest());
		dispatch(getAvailableReportTypesRequest());
		dispatch(getIsSwissPostIntegrationEnabledRequest());
		dispatch(getDashboardServicesRequest());
	}, [dispatch, organization]);

	// payment methods summary for the relink banner (once per merchant; silent: the banner is optional)
	useEffect(() => {
		if (!xMerchantId || paymentMethodsSummaryLoading) return;
		if (paymentMethodsSummary && paymentMethodsSummaryMerchantId === xMerchantId) return;
		dispatch(getSummaryRequest({ silent: true }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, xMerchantId]);

	useInterval(() => {
		dispatch(getNewMessageCountRequest());
	}, 20_000);

	const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
	const handleOpenNavMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElNav(event.currentTarget);
	};
	const handleCloseNavMenu = () => {
		setAnchorElNav(null);
	};

	const navEntries = useMemo(
		() =>
			getNavEntries({
				availableVatReportTypes,
				availableStandardVatReportTypes,
				isShipmentsEnabled,
				isEuDdpEnabled,
			}),
		[availableVatReportTypes, availableStandardVatReportTypes, isShipmentsEnabled, isEuDdpEnabled],
	);

	// Desktop nav overflow handling: items that don't fit in the bar are moved
	// into a "More" menu instead of wrapping onto a second line.
	const desktopNavRef = useRef<HTMLDivElement | null>(null);
	const measureRef = useRef<HTMLDivElement | null>(null);
	const [visibleNavEntries, setVisibleNavEntries] = useState(navEntries);
	const [overflowNavEntries, setOverflowNavEntries] = useState<typeof navEntries>([]);
	const [anchorElMore, setAnchorElMore] = useState<null | HTMLElement>(null);
	const handleOpenMoreMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElMore(event.currentTarget);
	};
	const handleCloseMoreMenu = () => {
		setAnchorElMore(null);
	};

	useLayoutEffect(() => {
		const recalc = () => {
			const container = desktopNavRef.current;
			const measure = measureRef.current;
			if (!container || !measure) return;

			const availableWidth = container.clientWidth;
			if (availableWidth === 0) {
				setVisibleNavEntries(navEntries);
				setOverflowNavEntries([]);
				return;
			}

			const children = Array.from(measure.children) as HTMLElement[];
			if (children.length === 0) {
				setVisibleNavEntries(navEntries);
				setOverflowNavEntries([]);
				return;
			}

			const itemWidths = children.map((el) => el.offsetWidth);
			const totalWidth = itemWidths.reduce((acc, w) => acc + w, 0);
			if (totalWidth <= availableWidth) {
				setVisibleNavEntries(navEntries);
				setOverflowNavEntries([]);
				return;
			}

			const moreButtonWidth = 40;
			const capacity = Math.max(availableWidth - moreButtonWidth, 0);
			let accWidth = 0;
			let cutIndex = navEntries.length;
			for (let i = 0; i < itemWidths.length; i += 1) {
				const next = accWidth + itemWidths[i];
				if (next <= capacity) {
					accWidth = next;
				} else {
					cutIndex = i;
					break;
				}
			}

			setVisibleNavEntries(navEntries.slice(0, cutIndex));
			setOverflowNavEntries(navEntries.slice(cutIndex));
		};

		recalc();
		const ro = new ResizeObserver(() => recalc());
		if (desktopNavRef.current) ro.observe(desktopNavRef.current);
		return () => {
			ro.disconnect();
		};
	}, [navEntries]);

	const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
	const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElUser(event.currentTarget);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-expect-error
		window.HubSpotConversations?.widget?.close();
	};
	const handleCloseUserMenu = () => {
		setAnchorElUser(null);
	};

	const [anchorElMessages, setAnchorElMessages] = useState<null | HTMLElement>(null);
	const handleOpenMessagesMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElMessages(event.currentTarget);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-expect-error
		window.HubSpotConversations?.widget?.close();
	};
	const handleCloseMessagesMenu = () => {
		setAnchorElMessages(null);
	};

	const handleLogout = () => {
		setAnchorElMessages(null);
		if (refreshToken) {
			dispatch(logoutRequest({ token: refreshToken }));
		}
	};

	const stringAvatar = (name: string) => {
		if (!name) return {};
		const children = name.includes(' ')
			? `${name?.split(' ')?.[0]?.[0]}${name?.split(' ')?.[1]?.[0]}`
			: name?.[0];
		return {
			sx: {
				bgcolor: '#fff',
				height: '30px',
				width: '30px',
				fontSize: '1.0rem',
				borderRadius: '8px',
				color: '#016193',
				fontWeight: 600,
			},
			children,
		};
	};

	return (
		<AppBar
			position="static"
			sx={{
				marginBottom: 2,
				backgroundColor: 'white',
				color: '#016193',
				zIndex: 1000,
				position: 'relative',
			}}
		>
			{/* <CardPaymentsBanner /> */}

			{lastInvoiceStatus?.status === 'overdue' && <PastDueBanner />}
			{/* {isShipmentsEnabled && <ServiceUnavailabilityBanner />} */}

			<Toolbar
				disableGutters
				sx={{
					margin: '0 15px',
					zIndex: 1000,
					'@media (min-width: 600px)': {
						minHeight: '56px',
					},
				}}
			>
				<Avatar
					alt="EAS Dashboard"
					src="/img/EAS-logo-new.svg"
					sx={{ display: { xs: 'none', md: 'flex' }, mr: 1, height: 42, width: 42 }}
				/>
				<Box
					sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }, justifyContent: 'space-between' }}
				>
					<IconButton
						size="large"
						aria-label="account of current user"
						aria-controls="menu-appbar"
						aria-haspopup="true"
						onClick={handleOpenNavMenu}
						color="inherit"
					>
						<MenuIcon />
					</IconButton>
					<Menu
						id="menu-appbar"
						anchorEl={anchorElNav}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'left',
						}}
						keepMounted
						transformOrigin={{
							vertical: 'top',
							horizontal: 'left',
						}}
						open={Boolean(anchorElNav)}
						onClose={handleCloseNavMenu}
						sx={{
							display: { xs: 'block', md: 'none' },
						}}
					>
						<NavigationItems
							isMobile
							handleCloseNavMenu={handleCloseNavMenu}
							availableVatReportTypes={availableVatReportTypes}
							availableStandardVatReportTypes={availableStandardVatReportTypes}
							isShipmentsEnabled={isShipmentsEnabled}
							isEuDdpEnabled={isEuDdpEnabled}
							location={location}
							navigate={navigate}
						/>
					</Menu>
				</Box>
				<Box
					ref={desktopNavRef}
					sx={{
						flexGrow: 1,
						minWidth: 0,
						display: { xs: 'none', md: 'flex' },
						alignItems: 'center',
						overflow: 'hidden',
					}}
				>
					{visibleNavEntries.map((entry) => (
						<NavEntryButton
							key={entry.path}
							entry={entry}
							isActive={isNavEntryActive(location.pathname, entry)}
							onClick={() => navigate(entry.path)}
						/>
					))}
					{overflowNavEntries.length > 0 && (
						<>
							<IconButton
								onClick={handleOpenMoreMenu}
								sx={{ color: '#016193', ml: 0.5, flexShrink: 0 }}
							>
								<MoreHorizIcon />
							</IconButton>
							<Menu
								anchorEl={anchorElMore}
								open={Boolean(anchorElMore)}
								onClose={handleCloseMoreMenu}
								anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
								transformOrigin={{ vertical: 'top', horizontal: 'left' }}
							>
								{overflowNavEntries.map((entry) => (
									<NavEntryButton
										key={entry.path}
										entry={entry}
										isActive={isNavEntryActive(location.pathname, entry)}
										onClick={() => {
											navigate(entry.path);
											handleCloseMoreMenu();
										}}
									/>
								))}
							</Menu>
						</>
					)}
				</Box>

				{/* Hidden measurement row used to calculate which nav entries fit */}
				<Box
					ref={measureRef}
					sx={{
						position: 'fixed',
						left: '-10000px',
						top: 0,
						visibility: 'hidden',
						pointerEvents: 'none',
						display: { xs: 'none', md: 'flex' },
						alignItems: 'center',
					}}
				>
					{navEntries.map((entry) => (
						<NavEntryButton
							key={`measure-${entry.path}`}
							entry={entry}
							isActive={false}
							onClick={() => {}}
						/>
					))}
				</Box>

				<Box sx={{ flexGrow: { xs: 1, sm: 1, md: 0 } }}>
					<Avatar
						alt="DASHBOARD"
						src="/img/EAS-logo-new.svg"
						sx={{ display: { xs: 'none', sm: 'flex', md: 'none' }, mr: 1, ml: 2 }}
					/>
				</Box>

				<Box
					sx={{
						flexGrow: 0,
						flexShrink: 0,
						display: 'flex',
						alignItems: 'center',
						flexWrap: 'nowrap',
					}}
				>
					<Tooltip title="Notifications">
						<IconButton
							sx={{ color: '#016193', mr: 1 }}
							onClick={handleOpenMessagesMenu}
							disabled={(messages?.data?.length ?? 0) === 0 && !newMessageCount}
						>
							<Badge badgeContent={newMessageCount || 0} color="error">
								<NotificationsIcon />
							</Badge>
						</IconButton>
					</Tooltip>
					{userinfo && (
						<Tooltip title="Open settings">
							<IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1,
										bgcolor: '#016193',
										color: 'white',
										p: '3px 3px 3px 10px',
										borderRadius: '12px',
										fontSize: '0.75rem',
										fontWeight: 550,
									}}
								>
									<span
										style={{
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											maxWidth: '220px',
											display: 'block',
										}}
									>
										{organization?.groupName}
									</span>
									<Avatar {...stringAvatar(userinfo?.name || '')} />
								</Box>
							</IconButton>
						</Tooltip>
					)}
					<UserMenu
						anchorElUser={anchorElUser}
						handleCloseUserMenu={handleCloseUserMenu}
						handleLogout={handleLogout}
					/>
					<MessagesMenu anchorEl={anchorElMessages} handleCloseMenu={handleCloseMessagesMenu} />
				</Box>
			</Toolbar>
		</AppBar>
	);
};
export default ResponsiveAppBar;
